import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertTriangle, DollarSign, CheckCircle, X, MapPin, Users, ChevronLeft, ChevronRight, RefreshCw, Cloud, Plus, Pencil, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const RealEstateManagingPartnerCalendar = () => {
  const [loading, setLoading] = useState(false);
  const [activeCalendar, setActiveCalendar] = useState('business');
  const [activeCategory, setActiveCategory] = useState('all');
  // Initialize to current month and year
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date()); // Default to today
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryModalType, setSummaryModalType] = useState('');
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [widgetModalType, setWidgetModalType] = useState('');
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState({ day: null, events: [] });
  
  // Outlook Desktop Sync State
  const [outlookEvents, setOutlookEvents] = useState([]);
  const [outlookSyncStatus, setOutlookSyncStatus] = useState({ synced: false, last_sync: null, event_count: 0 });
  const [syncingOutlook, setSyncingOutlook] = useState(false);
  const [showOutlookEvents, setShowOutlookEvents] = useState(true);
  
  // Nylas Calendar State (auto-sync from connected email accounts)
  const [nylasEvents, setNylasEvents] = useState([]);
  const [nylasAccounts, setNylasAccounts] = useState([]);
  const [syncingNylas, setSyncingNylas] = useState(false);
  const [showNylasEvents, setShowNylasEvents] = useState(true);
  
  // Active calendar account tab (for filtering by account)
  const [activeAccountTab, setActiveAccountTab] = useState('all'); // 'all' or account email
  
  // Calendar Connection State
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);
  const [showICloudCalendarModal, setShowICloudCalendarModal] = useState(false);
  const [showGoogleCalendarModal, setShowGoogleCalendarModal] = useState(false);
  const [showOutlookCalendarModal, setShowOutlookCalendarModal] = useState(false);
  const [icloudCalEmail, setIcloudCalEmail] = useState('');
  const [icloudCalPassword, setIcloudCalPassword] = useState('');
  const [calendarConnecting, setCalendarConnecting] = useState(false);
  const [calendarError, setCalendarError] = useState('');
  const [connectedCalendarType, setConnectedCalendarType] = useState(null);
  
  // Add Event State
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDescription, setNewEventDescription] = useState('');
  const [newEventLocation, setNewEventLocation] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventStartTime, setNewEventStartTime] = useState('09:00');
  const [newEventEndTime, setNewEventEndTime] = useState('10:00');
  const [newEventAllDay, setNewEventAllDay] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [createEventError, setCreateEventError] = useState('');
  
  // Editable Category State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('📅');
  const [newCategoryColor, setNewCategoryColor] = useState('blue');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryValue, setEditCategoryValue] = useState('');
  
  // Default calendar categories
  const defaultCalendarCategories = [
    { id: 'all', label: 'All', color: 'gray', icon: '📋' },
    { id: 'meeting', label: 'Meetings', color: 'blue', icon: '👥' },
    { id: 'work', label: 'Work', color: 'blue', icon: '💼' },
    { id: 'cresa', label: 'Cresa', color: 'emerald', icon: '🏢' },
    { id: 'birthday', label: 'Birthdays', color: 'pink', icon: '🎂' },
    { id: 'health', label: 'Health', color: 'red', icon: '⚕️' },
    { id: 'sports', label: 'Sports', color: 'orange', icon: '⚽' },
    { id: 'religious', label: 'Religious', color: 'purple', icon: '✝️' },
    { id: 'travel', label: 'Travel', color: 'cyan', icon: '✈️' },
    { id: 'home', label: 'Home', color: 'green', icon: '🏠' },
    { id: 'social', label: 'Social', color: 'yellow', icon: '🎉' },
    { id: 'education', label: 'Education', color: 'indigo', icon: '🎓' },
    { id: 'networking', label: 'Networking', color: 'teal', icon: '🤝' },
    { id: 'other', label: 'Other', color: 'gray', icon: '📁' }
  ];
  
  // Load custom categories from localStorage
  const [customCalendarCategories, setCustomCalendarCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('calendar_categories');
      return saved ? JSON.parse(saved) : defaultCalendarCategories;
    } catch { return defaultCalendarCategories; }
  });
  
  // Available emoji icons for calendar categories
  const calendarCategoryIcons = ['📅', '📋', '👥', '💼', '🏢', '🎂', '⚕️', '⚽', '✝️', '✈️', '🏠', '🎉', '🎓', '🤝', '📁', '🎯', '🌟', '💡', '🔔', '⏰', '🎵', '🎨', '📊', '💰', '🛒', '🍽️', '🎮', '🏋️', '📱', '💻'];
  
  // Available colors for categories
  const categoryColors = ['blue', 'emerald', 'pink', 'red', 'orange', 'purple', 'cyan', 'green', 'yellow', 'indigo', 'teal', 'gray'];
  
  // User ID for Nylas
  const userId = 'dwoodson';
  
  // Check if any calendar is connected on mount
  useEffect(() => {
    const storedCalType = localStorage.getItem('calendar_connected_type');
    if (storedCalType) {
      setIsCalendarConnected(true);
      setConnectedCalendarType(storedCalType);
    }
  }, []);
  
  // Get API URL - use window.location for production
  const getApiUrl = () => {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return process.env.REACT_APP_BACKEND_URL || '';
  };
  const apiUrl = getApiUrl();
  
  // Fetch Outlook desktop calendar events
  const fetchOutlookEvents = async () => {
    try {
      setSyncingOutlook(true);
      console.log('Fetching Outlook events for month:', currentMonth + 1, currentYear);
      const response = await fetch(`${apiUrl}/api/outlook-calendar/desktop-events-by-month/${currentYear}/${currentMonth + 1}`);
      const data = await response.json();
      console.log('Outlook events response:', data);
      
      if (data.status === 'success' && data.events_by_day) {
        // Convert events_by_day to flat array with day numbers
        const events = [];
        Object.entries(data.events_by_day).forEach(([day, dayEvents]) => {
          dayEvents.forEach(event => {
            events.push({
              ...event,
              day: parseInt(day),
              name: event.subject || event.title,
              source: 'outlook'
            });
          });
        });
        setOutlookEvents(events);
        setOutlookSyncStatus({
          synced: true,
          last_sync: data.synced_at,
          event_count: data.total_events
        });
      } else {
        setOutlookEvents([]);
        setOutlookSyncStatus({ synced: false, last_sync: null, event_count: 0 });
      }
    } catch (error) {
      console.error('Error fetching Outlook events:', error);
      setOutlookEvents([]);
    } finally {
      setSyncingOutlook(false);
    }
  };
  
  // Fetch Nylas connected accounts
  const fetchNylasAccounts = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/nylas/accounts?user_id=${userId}`);
      const data = await response.json();
      if (data.accounts && data.accounts.length > 0) {
        setNylasAccounts(data.accounts);
        console.log('📅 Nylas accounts found:', data.accounts.length);
        return data.accounts;
      }
      return [];
    } catch (error) {
      console.error('Error fetching Nylas accounts:', error);
      return [];
    }
  };
  
  // Fetch Nylas calendar events for all connected accounts
  const fetchNylasEvents = async () => {
    try {
      setSyncingNylas(true);
      
      // First get connected accounts
      const accounts = await fetchNylasAccounts();
      if (accounts.length === 0) {
        console.log('📅 No Nylas accounts connected');
        setNylasEvents([]);
        return;
      }
      
      // Fetch events for each account
      const allEvents = [];
      for (const account of accounts) {
        try {
          console.log(`📅 Fetching Nylas events for ${account.email}, month: ${currentMonth + 1}/${currentYear}`);
          const response = await fetch(
            `${apiUrl}/api/nylas/events/by-month/${currentYear}/${currentMonth + 1}?user_id=${userId}&email=${encodeURIComponent(account.email)}`
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success' && data.events_by_day) {
              // Convert events_by_day to flat array with day numbers
              Object.entries(data.events_by_day).forEach(([day, dayEvents]) => {
                dayEvents.forEach(event => {
                  allEvents.push({
                    ...event,
                    day: parseInt(day),
                    name: event.title,
                    source: 'nylas',
                    account_email: account.email
                  });
                });
              });
              console.log(`📅 Fetched ${data.total_events} events for ${account.email}`);
            }
          }
        } catch (err) {
          console.error(`Error fetching events for ${account.email}:`, err);
        }
      }
      
      setNylasEvents(allEvents);
      console.log(`📅 Total Nylas events: ${allEvents.length}`);
      
    } catch (error) {
      console.error('Error fetching Nylas events:', error);
      setNylasEvents([]);
    } finally {
      setSyncingNylas(false);
    }
  };
  
  // Check Outlook sync status on mount
  const checkOutlookSyncStatus = async () => {
    try {
      console.log('Checking Outlook sync status...');
      const response = await fetch(`${apiUrl}/api/outlook-calendar/desktop-sync-status`);
      const data = await response.json();
      console.log('Sync status:', data);
      setOutlookSyncStatus(data);
      if (data.synced) {
        fetchOutlookEvents();
      }
    } catch (error) {
      console.error('Error checking Outlook sync status:', error);
    }
  };
  
  // Handle iCloud Calendar connection
  const handleICloudCalendarConnect = async () => {
    if (!icloudCalEmail || !icloudCalPassword) {
      setCalendarError('Please enter both email and password');
      return;
    }
    
    setCalendarConnecting(true);
    setCalendarError('');
    
    try {
      const response = await fetch(`${apiUrl}/api/icloud-calendar/authenticate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apple_id: icloudCalEmail,
          app_password: icloudCalPassword
        })
      });
      
      if (response.ok) {
        setIsCalendarConnected(true);
        setConnectedCalendarType('icloud');
        localStorage.setItem('calendar_connected_type', 'icloud');
        localStorage.setItem('icloud_cal_email', icloudCalEmail);
        localStorage.setItem('icloud_cal_password', icloudCalPassword);
        setShowICloudCalendarModal(false);
        // Refresh calendar events
        fetchOutlookEvents();
      } else {
        const data = await response.json();
        setCalendarError(data.detail || 'Failed to connect. Please check your credentials.');
      }
    } catch (error) {
      setCalendarError('Connection failed. Please try again.');
    } finally {
      setCalendarConnecting(false);
    }
  };
  
  // Handle Google Calendar connect (show modal)
  const handleGoogleCalendarConnect = () => {
    setShowGoogleCalendarModal(true);
  };
  
  // Handle Outlook Calendar connect (show modal)
  const handleOutlookCalendarConnect = () => {
    setShowOutlookCalendarModal(true);
  };
  
  // Actually connect Outlook (from modal)
  const connectOutlookCalendar = () => {
    // Trigger existing Outlook sync
    fetchOutlookEvents();
    setIsCalendarConnected(true);
    setConnectedCalendarType('outlook');
    localStorage.setItem('calendar_connected_type', 'outlook');
    setShowOutlookCalendarModal(false);
  };
  
  // Create a new event via Nylas API
  const createNewEvent = async () => {
    if (!newEventTitle.trim()) {
      setCreateEventError('Please enter an event title');
      return;
    }
    if (!newEventDate) {
      setCreateEventError('Please select a date');
      return;
    }
    
    setCreatingEvent(true);
    setCreateEventError('');
    
    try {
      // First, get the primary calendar ID
      const calendarsResponse = await fetch(`${apiUrl}/api/nylas/calendars?user_id=${userId}`);
      const calendarsData = await calendarsResponse.json();
      
      if (!calendarsData.calendars || calendarsData.calendars.length === 0) {
        setCreateEventError('No calendars found. Please connect a calendar first.');
        return;
      }
      
      // Find the primary calendar or use the first writable one
      const primaryCalendar = calendarsData.calendars.find(c => c.is_primary && !c.read_only) 
        || calendarsData.calendars.find(c => !c.read_only);
      
      if (!primaryCalendar) {
        setCreateEventError('No writable calendar found. Please check your calendar permissions.');
        return;
      }
      
      // Build start and end timestamps
      const startDateTime = new Date(`${newEventDate}T${newEventStartTime}:00`);
      const endDateTime = new Date(`${newEventDate}T${newEventEndTime}:00`);
      
      const eventPayload = {
        title: newEventTitle,
        description: newEventDescription,
        location: newEventLocation,
        start_time: Math.floor(startDateTime.getTime() / 1000).toString(),
        end_time: Math.floor(endDateTime.getTime() / 1000).toString(),
        all_day: newEventAllDay,
        calendar_id: primaryCalendar.id
      };
      
      const response = await fetch(`${apiUrl}/api/nylas/events?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventPayload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === 'created') {
        // Success - close modal and refresh events
        setShowAddEventModal(false);
        resetEventForm();
        // Refresh events
        fetchNylasEvents();
        fetchOutlookEvents();
      } else {
        setCreateEventError(data.detail || 'Failed to create event');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      setCreateEventError('Failed to create event. Please try again.');
    } finally {
      setCreatingEvent(false);
    }
  };
  
  // Reset the add event form
  const resetEventForm = () => {
    setNewEventTitle('');
    setNewEventDescription('');
    setNewEventLocation('');
    setNewEventDate('');
    setNewEventStartTime('09:00');
    setNewEventEndTime('10:00');
    setNewEventAllDay(false);
    setCreateEventError('');
  };
  
  // Open add event modal with selected date
  const openAddEventModal = (date = null) => {
    if (date) {
      // Format date as YYYY-MM-DD
      const year = currentYear;
      const month = String(currentMonth + 1).padStart(2, '0');
      const day = String(date).padStart(2, '0');
      setNewEventDate(`${year}-${month}-${day}`);
    } else {
      // Default to today
      const today = new Date();
      setNewEventDate(today.toISOString().split('T')[0]);
    }
    setShowAddEventModal(true);
  };
  
  // Fetch Outlook and Nylas events when month/year changes
  useEffect(() => {
    if (apiUrl) {
      checkOutlookSyncStatus();
      fetchNylasEvents(); // Auto-sync Nylas calendar events
    }
  }, [currentMonth, currentYear, apiUrl]);

  const months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 
                  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
  
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  
  // Calculate the starting day of week dynamically for current month/year
  const getMonthStartDay = (month, year) => {
    return new Date(year, month, 1).getDay();
  };
  const monthStartDay = getMonthStartDay(currentMonth, currentYear);
  
  // Also calculate days in month dynamically (handles leap years)
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };
  const currentDaysInMonth = getDaysInMonth(currentMonth, currentYear);

  // Business calendar events by category (with full details for modal)
  const businessEvents = {
    all: [
      { day: 2, name: 'Empire State Closing', type: 'deal', color: 'green', time: '2:00 PM - 4:30 PM', location: 'Empire State Building, Suite 2400', description: 'Final closing for Tech Corp lease agreement. Review and sign all documents.', value: '$2.5M', attendees: ['Sarah Mitchell', 'John Martinez', 'Tech Corp Legal Team'] },
      { day: 2, name: 'Morning Team Standup', type: 'meeting', color: 'blue', time: '9:00 AM - 9:30 AM', location: 'Main Office', description: 'Daily team standup to review priorities.', value: 'N/A', attendees: ['All Team'] },
      { day: 4, name: 'Downtown Launch', type: 'meeting', color: 'blue', time: '10:00 AM - 12:00 PM', location: '450 Park Avenue', description: 'Marketing launch event for the new $15M luxury property downtown.', value: '$15M', attendees: ['Emily Chen', 'Marketing Team', 'Press'] },
      { day: 4, name: 'Investor Call', type: 'meeting', color: 'blue', time: '3:00 PM - 4:00 PM', location: 'Virtual - Zoom', description: 'Q1 investor update call to discuss portfolio performance.', value: '$50M Portfolio', attendees: ['Investors', 'Finance Team'] },
      { day: 6, name: 'Q1 Revenue Review', type: 'deadline', color: 'yellow', time: '2:00 PM - 4:00 PM', location: 'Conference Room B', description: 'Quarterly financial review meeting with the finance team.', value: 'N/A', attendees: ['Finance Team', 'Alex Rivera', 'Michael Wong'] },
      { day: 7, name: 'Site Visit - Tribeca', type: 'meeting', color: 'blue', time: '10:00 AM - 12:00 PM', location: 'Tribeca Property', description: 'Pre-acquisition site visit for potential Tribeca development.', value: '$18M', attendees: ['Development Team'] },
      { day: 8, name: 'Client Dinner', type: 'meeting', color: 'blue', time: '7:00 PM - 10:00 PM', location: 'The Capital Grille NYC', description: 'High-value client dinner to discuss upcoming property portfolio expansion.', value: '$8M Pipeline', attendees: ['Managing Partner', 'VIP Client', 'Sarah Mitchell'] },
      { day: 8, name: 'Morning Briefing', type: 'meeting', color: 'blue', time: '8:00 AM - 9:00 AM', location: 'Main Office', description: 'Executive briefing on key deals and market trends.', value: 'N/A', attendees: ['Executive Team'] },
      { day: 9, name: 'Legal Review', type: 'deadline', color: 'yellow', time: '5:00 PM', location: 'Legal Office', description: 'Submit contracts for legal review.', value: '$5.5M', attendees: ['Legal Team'] },
      { day: 11, name: 'Finance Meeting', type: 'meeting', color: 'blue', time: '2:00 PM - 3:30 PM', location: 'Conference A', description: 'Monthly finance review and budget planning.', value: 'N/A', attendees: ['CFO', 'Finance Team'] },
      { day: 12, name: 'Property Tour', type: 'meeting', color: 'blue', time: '11:00 AM - 1:00 PM', location: 'Brooklyn Industrial District', description: 'Warehouse property tour with potential investors.', value: '$4.2M', attendees: ['Investor Group', 'John Martinez'] },
      { day: 12, name: 'Vendor Meeting', type: 'meeting', color: 'blue', time: '3:00 PM - 4:00 PM', location: 'Main Office', description: 'Review vendor contracts and performance.', value: 'N/A', attendees: ['Operations Team'] },
      { day: 13, name: 'Marketing Strategy', type: 'meeting', color: 'blue', time: '1:00 PM - 3:00 PM', location: 'Marketing Dept', description: 'Q2 marketing strategy planning session.', value: '$2M Budget', attendees: ['Marketing Team', 'Emily Chen'] },
      { day: 15, name: 'Contract Deadline', type: 'deadline', color: 'yellow', time: '5:00 PM', location: 'Legal Office', description: 'Final deadline to submit Chelsea property contract revisions.', value: '$3.1M', attendees: ['Legal Team'] },
      { day: 15, name: 'Board Meeting', type: 'meeting', color: 'blue', time: '10:00 AM - 12:00 PM', location: 'Board Room', description: 'Monthly board meeting to review company performance.', value: 'N/A', attendees: ['Board Members', 'Executive Team'] },
      { day: 16, name: 'Client Presentation', type: 'meeting', color: 'blue', time: '2:00 PM - 4:00 PM', location: 'Client Office', description: 'Present new property portfolio to potential client.', value: '$12M', attendees: ['Sales Team', 'Client'] },
      { day: 18, name: 'Brooklyn Deal Close', type: 'deal', color: 'green', time: '3:00 PM - 5:00 PM', location: 'Brooklyn Office', description: 'Closing ceremony for the Brooklyn warehouse acquisition.', value: '$4.2M', attendees: ['Emily Chen', 'Buyer Representatives', 'Legal'] },
      { day: 19, name: 'Construction Review', type: 'meeting', color: 'blue', time: '9:00 AM - 11:00 AM', location: 'Construction Site', description: 'Monthly construction progress review.', value: '$8M Project', attendees: ['Construction Team', 'Architects'] },
      { day: 20, name: 'Networking Event', type: 'meeting', color: 'blue', time: '6:00 PM - 9:00 PM', location: 'Plaza Hotel', description: 'Real estate industry networking event.', value: 'N/A', attendees: ['Industry Contacts'] },
      { day: 22, name: 'Team Meeting', type: 'meeting', color: 'blue', time: '9:00 AM - 10:30 AM', location: 'Main Office', description: 'Weekly team sync-up to review active deals and pipeline.', value: 'N/A', attendees: ['All Team Members'] },
      { day: 22, name: 'Property Valuation', type: 'deadline', color: 'yellow', time: '3:00 PM', location: 'Appraisal Office', description: 'Receive property valuation report.', value: '$7.5M', attendees: ['Appraisers'] },
      { day: 23, name: 'Downtown Site Visit', type: 'meeting', color: 'blue', time: '11:00 AM - 1:00 PM', location: 'Downtown Manhattan', description: 'Visit potential acquisition site.', value: '$22M', attendees: ['Investment Team'] },
      { day: 25, name: 'Zoning Approval Due', type: 'deadline', color: 'yellow', time: 'EOD', location: 'City Planning Office', description: 'Submit final zoning approval application for SoHo development.', value: '$12M Project', attendees: ['Development Team'] },
      { day: 26, name: 'Partnership Meeting', type: 'meeting', color: 'blue', time: '2:00 PM - 4:00 PM', location: 'Partner Office', description: 'Discuss potential partnership opportunities.', value: '$30M Joint Venture', attendees: ['Partners', 'Legal'] },
      { day: 28, name: 'Madison Ave Closing', type: 'deal', color: 'green', time: '1:00 PM - 3:00 PM', location: 'Madison Avenue Office', description: 'Final closing for the Madison Avenue commercial property deal.', value: '$6.2M', attendees: ['Alex Rivera', 'Buyer Team', 'Legal'] },
      { day: 29, name: 'Strategy Session', type: 'meeting', color: 'blue', time: '10:00 AM - 1:00 PM', location: 'Conference A', description: 'Q2 strategy planning session.', value: 'N/A', attendees: ['Executive Team'] },
      { day: 30, name: 'Month-End Review', type: 'deadline', color: 'yellow', time: '5:00 PM', location: 'Finance Dept', description: 'Submit month-end financial reports.', value: 'N/A', attendees: ['Finance Team'] },
    ],
    deals: [
      { day: 2, name: 'Empire State Closing', type: 'deal', color: 'green', time: '2:00 PM - 4:30 PM', location: 'Empire State Building, Suite 2400', description: 'Final closing for Tech Corp lease agreement. Review and sign all documents.', value: '$2.5M', attendees: ['Sarah Mitchell', 'John Martinez', 'Tech Corp Legal Team'] },
      { day: 18, name: 'Brooklyn Deal Close', type: 'deal', color: 'green', time: '3:00 PM - 5:00 PM', location: 'Brooklyn Office', description: 'Closing ceremony for the Brooklyn warehouse acquisition.', value: '$4.2M', attendees: ['Emily Chen', 'Buyer Representatives', 'Legal'] },
      { day: 28, name: 'Madison Ave Closing', type: 'deal', color: 'green', time: '1:00 PM - 3:00 PM', location: 'Madison Avenue Office', description: 'Final closing for the Madison Avenue commercial property deal.', value: '$6.2M', attendees: ['Alex Rivera', 'Buyer Team', 'Legal'] },
    ],
    meetings: [
      { day: 2, name: 'Morning Team Standup', type: 'meeting', color: 'blue', time: '9:00 AM - 9:30 AM', location: 'Main Office', description: 'Daily team standup to review priorities.', value: 'N/A', attendees: ['All Team'] },
      { day: 4, name: 'Downtown Launch', type: 'meeting', color: 'blue', time: '10:00 AM - 12:00 PM', location: '450 Park Avenue', description: 'Marketing launch event for the new $15M luxury property downtown.', value: '$15M', attendees: ['Emily Chen', 'Marketing Team', 'Press'] },
      { day: 4, name: 'Investor Call', type: 'meeting', color: 'blue', time: '3:00 PM - 4:00 PM', location: 'Virtual - Zoom', description: 'Q1 investor update call to discuss portfolio performance.', value: '$50M Portfolio', attendees: ['Investors', 'Finance Team'] },
      { day: 7, name: 'Site Visit - Tribeca', type: 'meeting', color: 'blue', time: '10:00 AM - 12:00 PM', location: 'Tribeca Property', description: 'Pre-acquisition site visit for potential Tribeca development.', value: '$18M', attendees: ['Development Team'] },
      { day: 8, name: 'Client Dinner', type: 'meeting', color: 'blue', time: '7:00 PM - 10:00 PM', location: 'The Capital Grille NYC', description: 'High-value client dinner to discuss upcoming property portfolio expansion.', value: '$8M Pipeline', attendees: ['Managing Partner', 'VIP Client', 'Sarah Mitchell'] },
      { day: 8, name: 'Morning Briefing', type: 'meeting', color: 'blue', time: '8:00 AM - 9:00 AM', location: 'Main Office', description: 'Executive briefing on key deals and market trends.', value: 'N/A', attendees: ['Executive Team'] },
      { day: 11, name: 'Finance Meeting', type: 'meeting', color: 'blue', time: '2:00 PM - 3:30 PM', location: 'Conference A', description: 'Monthly finance review and budget planning.', value: 'N/A', attendees: ['CFO', 'Finance Team'] },
      { day: 12, name: 'Property Tour', type: 'meeting', color: 'blue', time: '11:00 AM - 1:00 PM', location: 'Brooklyn Industrial District', description: 'Warehouse property tour with potential investors.', value: '$4.2M', attendees: ['Investor Group', 'John Martinez'] },
      { day: 12, name: 'Vendor Meeting', type: 'meeting', color: 'blue', time: '3:00 PM - 4:00 PM', location: 'Main Office', description: 'Review vendor contracts and performance.', value: 'N/A', attendees: ['Operations Team'] },
      { day: 13, name: 'Marketing Strategy', type: 'meeting', color: 'blue', time: '1:00 PM - 3:00 PM', location: 'Marketing Dept', description: 'Q2 marketing strategy planning session.', value: '$2M Budget', attendees: ['Marketing Team', 'Emily Chen'] },
      { day: 15, name: 'Board Meeting', type: 'meeting', color: 'blue', time: '10:00 AM - 12:00 PM', location: 'Board Room', description: 'Monthly board meeting to review company performance.', value: 'N/A', attendees: ['Board Members', 'Executive Team'] },
      { day: 16, name: 'Client Presentation', type: 'meeting', color: 'blue', time: '2:00 PM - 4:00 PM', location: 'Client Office', description: 'Present new property portfolio to potential client.', value: '$12M', attendees: ['Sales Team', 'Client'] },
      { day: 19, name: 'Construction Review', type: 'meeting', color: 'blue', time: '9:00 AM - 11:00 AM', location: 'Construction Site', description: 'Monthly construction progress review.', value: '$8M Project', attendees: ['Construction Team', 'Architects'] },
      { day: 20, name: 'Networking Event', type: 'meeting', color: 'blue', time: '6:00 PM - 9:00 PM', location: 'Plaza Hotel', description: 'Real estate industry networking event.', value: 'N/A', attendees: ['Industry Contacts'] },
      { day: 22, name: 'Team Meeting', type: 'meeting', color: 'blue', time: '9:00 AM - 10:30 AM', location: 'Main Office', description: 'Weekly team sync-up to review active deals and pipeline.', value: 'N/A', attendees: ['All Team Members'] },
      { day: 23, name: 'Downtown Site Visit', type: 'meeting', color: 'blue', time: '11:00 AM - 1:00 PM', location: 'Downtown Manhattan', description: 'Visit potential acquisition site.', value: '$22M', attendees: ['Investment Team'] },
      { day: 26, name: 'Partnership Meeting', type: 'meeting', color: 'blue', time: '2:00 PM - 4:00 PM', location: 'Partner Office', description: 'Discuss potential partnership opportunities.', value: '$30M Joint Venture', attendees: ['Partners', 'Legal'] },
      { day: 29, name: 'Strategy Session', type: 'meeting', color: 'blue', time: '10:00 AM - 1:00 PM', location: 'Conference A', description: 'Q2 strategy planning session.', value: 'N/A', attendees: ['Executive Team'] },
    ],
    deadlines: [
      { day: 6, name: 'Q1 Revenue Review', type: 'deadline', color: 'yellow', time: '2:00 PM - 4:00 PM', location: 'Conference Room B', description: 'Quarterly financial review meeting with the finance team.', value: 'N/A', attendees: ['Finance Team', 'Alex Rivera', 'Michael Wong'] },
      { day: 9, name: 'Legal Review', type: 'deadline', color: 'yellow', time: '5:00 PM', location: 'Legal Office', description: 'Submit contracts for legal review.', value: '$5.5M', attendees: ['Legal Team'] },
      { day: 15, name: 'Contract Deadline', type: 'deadline', color: 'yellow', time: '5:00 PM', location: 'Legal Office', description: 'Final deadline to submit Chelsea property contract revisions.', value: '$3.1M', attendees: ['Legal Team'] },
      { day: 22, name: 'Property Valuation', type: 'deadline', color: 'yellow', time: '3:00 PM', location: 'Appraisal Office', description: 'Receive property valuation report.', value: '$7.5M', attendees: ['Appraisers'] },
      { day: 25, name: 'Zoning Approval Due', type: 'deadline', color: 'yellow', time: 'EOD', location: 'City Planning Office', description: 'Submit final zoning approval application for SoHo development.', value: '$12M Project', attendees: ['Development Team'] },
      { day: 30, name: 'Month-End Review', type: 'deadline', color: 'yellow', time: '5:00 PM', location: 'Finance Dept', description: 'Submit month-end financial reports.', value: 'N/A', attendees: ['Finance Team'] },
    ],
  };

  // Personal calendar events by category (with full details for modal)
  const personalEvents = {
    all: [
      { day: 3, name: 'Gym Session', type: 'fitness', color: 'orange', time: '6:00 AM - 7:30 AM', location: 'Equinox Upper East Side', description: 'Morning strength training and cardio workout.', notes: 'Focus on upper body today', attendees: ['Personal Trainer - Mike'] },
      { day: 5, name: 'Doctor Appointment', type: 'health', color: 'red', time: '3:00 PM - 4:00 PM', location: 'Manhattan Medical Center', description: 'Annual physical checkup with Dr. Johnson.', notes: 'Bring previous lab results', attendees: ['Dr. Sarah Johnson'] },
      { day: 10, name: 'Birthday Party', type: 'social', color: 'pink', time: '7:00 PM - 11:00 PM', location: "Friend's Apartment - Chelsea", description: "Celebrating Emma's 30th birthday with close friends.", notes: 'Bring gift - wine set', attendees: ['Emma', 'Close Friends Group'] },
      { day: 14, name: 'Yoga Class', type: 'fitness', color: 'orange', time: '8:00 AM - 9:00 AM', location: 'Yoga Studio Downtown', description: 'Vinyasa flow class for flexibility and mindfulness.', notes: 'Remember yoga mat', attendees: ['Yoga Instructor - Lisa'] },
      { day: 17, name: 'Family Dinner', type: 'family', color: 'purple', time: '6:00 PM - 9:00 PM', location: 'Home', description: 'Monthly family dinner with parents and siblings.', notes: 'Mom is making lasagna', attendees: ['Parents', 'Siblings'] },
      { day: 20, name: 'Dentist Visit', type: 'health', color: 'red', time: '2:00 PM - 3:00 PM', location: 'Smile Dental Care', description: 'Regular teeth cleaning and checkup.', notes: '6-month cleaning', attendees: ['Dr. Mark Chen'] },
      { day: 24, name: 'Movie Night', type: 'social', color: 'pink', time: '8:00 PM - 11:00 PM', location: 'AMC Lincoln Square', description: 'Watching the new sci-fi blockbuster with friends.', notes: 'Get tickets early', attendees: ['Friend Group'] },
      { day: 27, name: 'Parents Visit', type: 'family', color: 'purple', time: '12:00 PM - 8:00 PM', location: 'Your Apartment', description: 'Parents visiting for the day, showing them around the city.', notes: 'Clean apartment beforehand', attendees: ['Mom', 'Dad'] },
    ],
    fitness: [
      { day: 3, name: 'Gym Session', type: 'fitness', color: 'orange', time: '6:00 AM - 7:30 AM', location: 'Equinox Upper East Side', description: 'Morning strength training and cardio workout.', notes: 'Focus on upper body today', attendees: ['Personal Trainer - Mike'] },
      { day: 14, name: 'Yoga Class', type: 'fitness', color: 'orange', time: '8:00 AM - 9:00 AM', location: 'Yoga Studio Downtown', description: 'Vinyasa flow class for flexibility and mindfulness.', notes: 'Remember yoga mat', attendees: ['Yoga Instructor - Lisa'] },
    ],
    health: [
      { day: 5, name: 'Doctor Appointment', type: 'health', color: 'red', time: '3:00 PM - 4:00 PM', location: 'Manhattan Medical Center', description: 'Annual physical checkup with Dr. Johnson.', notes: 'Bring previous lab results', attendees: ['Dr. Sarah Johnson'] },
      { day: 20, name: 'Dentist Visit', type: 'health', color: 'red', time: '2:00 PM - 3:00 PM', location: 'Smile Dental Care', description: 'Regular teeth cleaning and checkup.', notes: '6-month cleaning', attendees: ['Dr. Mark Chen'] },
    ],
    social: [
      { day: 10, name: 'Birthday Party', type: 'social', color: 'pink', time: '7:00 PM - 11:00 PM', location: "Friend's Apartment - Chelsea", description: "Celebrating Emma's 30th birthday with close friends.", notes: 'Bring gift - wine set', attendees: ['Emma', 'Close Friends Group'] },
      { day: 24, name: 'Movie Night', type: 'social', color: 'pink', time: '8:00 PM - 11:00 PM', location: 'AMC Lincoln Square', description: 'Watching the new sci-fi blockbuster with friends.', notes: 'Get tickets early', attendees: ['Friend Group'] },
    ],
    family: [
      { day: 17, name: 'Family Dinner', type: 'family', color: 'purple', time: '6:00 PM - 9:00 PM', location: 'Home', description: 'Monthly family dinner with parents and siblings.', notes: 'Mom is making lasagna', attendees: ['Parents', 'Siblings'] },
      { day: 27, name: 'Parents Visit', type: 'family', color: 'purple', time: '12:00 PM - 8:00 PM', location: 'Your Apartment', description: 'Parents visiting for the day, showing them around the city.', notes: 'Clean apartment beforehand', attendees: ['Mom', 'Dad'] },
    ],
  };

  const currentEvents = activeCalendar === 'business' ? businessEvents : personalEvents;
  const businessCategories = ['all', 'deals', 'meetings', 'deadlines'];
  const personalCategories = ['all', 'fitness', 'health', 'social', 'family'];
  const availableCategories = activeCalendar === 'business' ? businessCategories : personalCategories;

  const handleEventClick = (event, day) => {
    setSelectedEvent({...event, day, month: months[currentMonth], year: currentYear});
    setShowEventModal(true);
  };

  const handleCalendarSwitch = (calendarType) => {
    setActiveCalendar(calendarType);
    if (calendarType === 'personal' && !personalCategories.includes(activeCategory)) {
      setActiveCategory('all');
    } else if (calendarType === 'business' && !businessCategories.includes(activeCategory)) {
      setActiveCategory('all');
    }
  };

  // Use custom calendar categories (loaded from localStorage or defaults)
  const outlookCategories = customCalendarCategories;
  
  // Category management functions
  const addCalendarCategory = () => {
    if (!newCategoryName.trim()) return;
    
    const categoryId = newCategoryName.toLowerCase().replace(/\s+/g, '-');
    
    // Check if category already exists
    if (customCalendarCategories.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    
    const newCategory = {
      id: categoryId,
      label: newCategoryName,
      color: newCategoryColor,
      icon: newCategoryIcon
    };
    
    const updatedCategories = [...customCalendarCategories, newCategory];
    setCustomCalendarCategories(updatedCategories);
    localStorage.setItem('calendar_categories', JSON.stringify(updatedCategories));
    setNewCategoryName('');
    setNewCategoryIcon('📅');
    setNewCategoryColor('blue');
    toast.success(`Added "${newCategoryName}" category`);
  };
  
  const updateCalendarCategory = (oldId, newName, newIcon, newColor) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    
    const updatedCategories = customCalendarCategories.map(c => {
      if (c.id === oldId) {
        return { ...c, id: newId, label: newName, icon: newIcon || c.icon, color: newColor || c.color };
      }
      return c;
    });
    
    setCustomCalendarCategories(updatedCategories);
    localStorage.setItem('calendar_categories', JSON.stringify(updatedCategories));
    if (activeCategory === oldId) setActiveCategory(newId);
    setEditingCategory(null);
    setEditCategoryValue('');
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deleteCalendarCategory = (categoryId) => {
    // Protected categories that cannot be deleted
    const protectedCategories = ['all', 'meeting', 'work', 'other'];
    if (protectedCategories.includes(categoryId)) {
      toast.error('Cannot delete default category');
      return;
    }
    
    const updatedCategories = customCalendarCategories.filter(c => c.id !== categoryId);
    setCustomCalendarCategories(updatedCategories);
    localStorage.setItem('calendar_categories', JSON.stringify(updatedCategories));
    if (activeCategory === categoryId) setActiveCategory('all');
    toast.success('Category deleted');
  };
  
  // Get category counts from Outlook events
  const getCategoryCounts = () => {
    const counts = {};
    outlookEvents.forEach(event => {
      const cat = event.category || event.type || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  };
  const categoryCounts = getCategoryCounts();
  
  // Filter categories that have events
  const availableOutlookCategories = outlookCategories.filter(
    cat => cat.id === 'all' || categoryCounts[cat.id] > 0
  );

  const getEventsForDay = (day) => {
    let events = [];
    
    // Helper function to parse time for sorting
    const parseTimeForSort = (event) => {
      const timeStr = event.time || event.start_time || '';
      if (!timeStr) return 9999; // Events without time go to end
      
      // Handle Unix timestamp
      if (typeof timeStr === 'number') {
        const date = new Date(timeStr * 1000);
        return date.getHours() * 60 + date.getMinutes();
      }
      
      // Handle HH:MM format (with optional AM/PM)
      const match = String(timeStr).match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const ampm = match[3];
        
        // Convert 12-hour to 24-hour if AM/PM present
        if (ampm) {
          if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
          if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
        }
        
        return hours * 60 + minutes;
      }
      return 9999;
    };
    
    // Format time from Unix timestamp or ISO string
    const formatEventTime = (timestamp) => {
      if (!timestamp) return '';
      if (typeof timestamp === 'number') {
        const date = new Date(timestamp * 1000);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      }
      return timestamp;
    };
    
    // If Outlook is synced and showing, use Outlook events
    if (outlookSyncStatus.synced && showOutlookEvents) {
      // Only include Outlook events if 'all' or 'outlook' tab is selected
      if (activeAccountTab === 'all' || activeAccountTab === 'outlook') {
        // Get Outlook events for this day, filtered by category
        const outlookDayEvents = outlookEvents
          .filter(event => {
            if (event.day !== day) return false;
            if (activeCategory !== 'all') {
              const eventCat = event.category || event.type || 'other';
              if (eventCat !== activeCategory) return false;
            }
            return true;
          })
          .map(event => ({
            ...event,
            name: event.subject || event.title || 'Outlook Event',
            source: 'outlook'
          }));
        events = [...events, ...outlookDayEvents];
      }
    }
    
    // Add Nylas events if showing
    if (showNylasEvents && nylasEvents.length > 0) {
      // Only include Nylas events if 'all' tab or specific Nylas account tab is selected
      if (activeAccountTab === 'all' || (activeAccountTab !== 'outlook' && nylasAccounts.some(a => a.email === activeAccountTab))) {
        const nylasDayEvents = nylasEvents
          .filter(event => {
            if (event.day !== day) return false;
            // Filter by account tab if not 'all' and not 'outlook'
            if (activeAccountTab !== 'all' && activeAccountTab !== 'outlook' && event.account_email !== activeAccountTab) {
              return false;
            }
            if (activeCategory !== 'all') {
              // Map Nylas events to categories if needed
              return true; // Show all Nylas events for now
            }
            return true;
          })
          .map(event => ({
            ...event,
            name: event.title || event.name || 'Calendar Event',
            time: formatEventTime(event.start_time),
            source: 'nylas',
            color: 'purple' // Distinguish Nylas events with purple
          }));
        events = [...events, ...nylasDayEvents];
      }
    }
    
    // If no synced events, use static events as fallback
    if (events.length === 0 && !outlookSyncStatus.synced && nylasEvents.length === 0) {
      const staticEvents = currentEvents[activeCategory] || currentEvents.all || [];
      events = staticEvents
        .filter(event => event.day === day)
        .map(event => ({
          ...event,
          source: 'static'
        }));
    }
    
    // Deduplicate events by name and time to prevent duplicates
    const uniqueEvents = events.reduce((acc, event) => {
      const eventKey = `${event.name}-${event.time || ''}-${event.start_time || ''}`.toLowerCase();
      if (!acc.some(e => `${e.name}-${e.time || ''}-${e.start_time || ''}`.toLowerCase() === eventKey)) {
        acc.push(event);
      }
      return acc;
    }, []);
    
    // Sort events by time (earliest first)
    return uniqueEvents.sort((a, b) => parseTimeForSort(a) - parseTimeForSort(b));
  };

  // Handler to show all events for a day
  const handleShowDayEvents = (day, events) => {
    setSelectedDayEvents({ day, events });
    setShowDayEventsModal(true);
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'all': 'gray',
      'meeting': 'blue',
      'work': 'blue',
      'cresa': 'emerald',
      'birthday': 'pink',
      'health': 'red',
      'sports': 'orange',
      'religious': 'purple',
      'travel': 'cyan',
      'home': 'green',
      'social': 'yellow',
      'education': 'indigo',
      'networking': 'teal',
      'other': 'gray'
    };
    return colors[category] || 'gray';
  };
  
  const getColorClasses = (color) => {
    const colorMap = {
      'blue': 'bg-blue-600 border-blue-400',
      'red': 'bg-red-600 border-red-400',
      'orange': 'bg-orange-500 border-orange-400',
      'pink': 'bg-pink-500 border-pink-400',
      'purple': 'bg-purple-600 border-purple-400',
      'cyan': 'bg-cyan-500 border-cyan-400',
      'green': 'bg-green-600 border-green-400',
      'yellow': 'bg-yellow-500 border-yellow-400',
      'indigo': 'bg-indigo-600 border-indigo-400',
      'teal': 'bg-teal-500 border-teal-400',
      'emerald': 'bg-emerald-500 border-emerald-400',
      'gray': 'bg-gray-600 border-gray-400'
    };
    return colorMap[color] || 'bg-gray-600 border-gray-400';
  };

  const getCategoryLabel = (category) => {
    const cat = outlookCategories.find(c => c.id === category);
    return cat ? cat.label : category;
  };

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6 overflow-hidden flex flex-col">
      {/* Calendar Connection Panel - Show when iCloud or Google not connected */}
      {!isCalendarConnected && (
        <div className="mb-4 bg-gradient-to-r from-purple-900/50 to-blue-900/50 border border-purple-500/30 rounded-xl p-4 md:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1">
              <h2 className="text-lg md:text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Calendar className="text-purple-400" size={24} />
                Connect Your Calendar
              </h2>
              <p className="text-gray-300 text-sm">
                {outlookSyncStatus.synced 
                  ? 'Outlook is connected. Add iCloud or Google to sync more calendars.'
                  : 'Sync your calendar from iCloud, Google, or Outlook to see all your events in one place.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              <button 
                onClick={() => setShowICloudCalendarModal(true)}
                className="flex-1 md:flex-initial bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg"
                data-testid="connect-icloud-calendar-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                iCloud
              </button>
              <button 
                onClick={handleGoogleCalendarConnect}
                className="flex-1 md:flex-initial bg-white hover:bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg"
                data-testid="connect-google-calendar-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>
              {!outlookSyncStatus.synced && (
                <button 
                  onClick={handleOutlookCalendarConnect}
                  className="flex-1 md:flex-initial bg-[#0078D4] hover:bg-[#106EBE] text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg"
                  data-testid="connect-outlook-calendar-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576zM14.637 7.387v13.226H.786c-.226 0-.42-.076-.578-.23C.05 20.23 0 20.036 0 19.81V5.618c0-.226.076-.42.23-.577C.383 4.888.577 4.81.803 4.81h13.834v2.577zm-2.46 5.618c0-.805-.284-1.49-.854-2.056-.57-.566-1.257-.85-2.06-.85-.805 0-1.492.284-2.062.85-.57.566-.855 1.251-.855 2.056 0 .8.285 1.485.855 2.051.57.566 1.257.85 2.062.85.803 0 1.49-.284 2.06-.85.57-.566.854-1.251.854-2.051zm-1.23 0c0 .453-.16.84-.484 1.163-.32.323-.71.484-1.166.484-.457 0-.847-.161-1.17-.484-.324-.322-.486-.71-.486-1.163 0-.453.162-.84.486-1.163.323-.323.713-.484 1.17-.484.456 0 .845.161 1.166.484.323.322.484.71.484 1.163z"/>
                  </svg>
                  Outlook
                </button>
              )}
              {outlookSyncStatus.synced && (
                <button 
                  onClick={handleOutlookCalendarConnect}
                  className="flex-1 md:flex-initial bg-green-600/20 hover:bg-green-600/30 text-green-400 px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 border border-green-500/30 transition-colors cursor-pointer"
                  data-testid="outlook-connected-btn"
                >
                  <CheckCircle size={18} />
                  Outlook Connected
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Header - Outside Container */}
      <div className="md:hidden flex items-center justify-between mb-3">
        <h1 className="text-xl font-bold mokoto-font">{months[currentMonth]} {currentYear}</h1>
        <button 
          className="p-2 text-white hover:text-gray-300 focus:outline-none transition-colors"
          onClick={() => {/* This would toggle sidebar - handled by parent */}}
        >
          <div className="flex flex-col gap-1.5">
            <div className="w-6 h-0.5 bg-current"></div>
            <div className="w-6 h-0.5 bg-current"></div>
            <div className="w-6 h-0.5 bg-current"></div>
          </div>
        </button>
      </div>

      {/* Top Bar - Month/Year and View Toggles */}
      {/* Unified Header Container */}
      <div className="mb-3 md:mb-6 bg-black border border-gray-400 rounded-xl p-3 md:p-4 flex-shrink-0">
        {/* Row 1: Title (desktop only), View Toggles, Navigation */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3 md:mb-4">
          {/* Desktop Title - Hidden on Mobile */}
          <h1 className="hidden md:block text-xl md:text-2xl font-bold mokoto-font">{months[currentMonth]} {currentYear}</h1>

          <div className="flex flex-wrap items-center gap-2 md:gap-4 w-full sm:w-auto">
            {/* Outlook Sync Button */}
            <div className="flex items-center gap-1 md:gap-2">
              <button
                onClick={fetchOutlookEvents}
                disabled={syncingOutlook}
                className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-semibold transition-colors ${
                  outlookSyncStatus.synced 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
              >
                {syncingOutlook ? (
                  <RefreshCw size={12} className="animate-spin" />
                ) : (
                  <Cloud size={12} />
                )}
                <span className="hidden sm:inline">{outlookSyncStatus.synced ? 'Refresh Outlook' : 'Load Outlook'}</span>
                <span className="sm:hidden">{outlookSyncStatus.synced ? 'Refresh' : 'Load'}</span>
              </button>
              {outlookSyncStatus.synced && (
                <button
                  onClick={() => setShowOutlookEvents(!showOutlookEvents)}
                  className={`px-1.5 md:px-2 py-1 md:py-1.5 rounded-lg text-[10px] md:text-xs font-semibold transition-colors ${
                    showOutlookEvents 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500' 
                      : 'bg-gray-800 text-gray-400 border border-gray-600'
                  }`}
                >
                  {showOutlookEvents ? 'Hide' : 'Show'} ({outlookSyncStatus.event_count})
                </button>
              )}
            </div>
            
            {/* View Toggle Buttons */}
            <div className="flex gap-1">
              <button
                className="px-2 md:px-4 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-semibold transition-colors bg-white text-black"
              >
                Month
              </button>
              <button
                onClick={() => window.location.href = '/calendar-week'}
                className="px-2 md:px-4 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-semibold transition-colors bg-gray-800 text-white hover:bg-gray-700"
              >
                Week
              </button>
            </div>

            {/* Navigation Arrows */}
            <div className="flex items-center gap-1">
              <button 
                onClick={handlePrevMonth}
                className="p-1 md:p-1.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button 
                onClick={() => {
                  const today = new Date();
                  setCurrentMonth(today.getMonth());
                  setCurrentYear(today.getFullYear());
                }}
                className="px-2 md:px-3 py-1 md:py-1.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors text-[10px] md:text-sm"
              >
                Today
              </button>
              <button 
                onClick={handleNextMonth}
                className="p-1 md:p-1.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            
            {/* Add Event Button */}
            <button
              onClick={() => openAddEventModal()}
              data-testid="add-event-btn"
              className="px-3 md:px-4 py-1.5 md:py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs md:text-sm font-semibold transition-colors flex items-center gap-1.5"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add Event</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>
        
        {/* Row 2: Sync Status */}
        {outlookSyncStatus.synced && outlookSyncStatus.last_sync && (
          <div className="mb-2 md:mb-3 text-[10px] md:text-sm text-gray-400">
            <Cloud size={12} className="inline mr-1 md:mr-2" />
            Outlook: Last sync {new Date(outlookSyncStatus.last_sync).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })} CT
          </div>
        )}
        
        {/* Nylas Sync Status */}
        {nylasAccounts.length > 0 && (
          <div className="mb-2 md:mb-3 text-[10px] md:text-sm text-gray-400 flex items-center gap-2">
            <div className="flex items-center">
              <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
              Connected: {nylasAccounts.map(a => a.email).join(', ')} ({nylasEvents.length} events)
            </div>
            <button
              onClick={fetchNylasEvents}
              disabled={syncingNylas}
              className="p-1 rounded hover:bg-gray-700 transition-colors"
              title="Refresh Nylas events"
            >
              <RefreshCw size={12} className={syncingNylas ? 'animate-spin text-purple-400' : 'text-gray-400'} />
            </button>
          </div>
        )}

        {/* Calendar Account Tabs - Show when Nylas accounts are connected OR Outlook is synced */}
        {(nylasAccounts.length > 0 || outlookSyncStatus.synced) && (
          <div className="mb-3 md:mb-4" data-testid="calendar-account-tabs">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={14} className="text-purple-400" />
              <span className="text-xs md:text-sm font-semibold text-gray-400">Calendar Accounts</span>
            </div>
            <div className="flex gap-2 flex-wrap overflow-x-auto pb-1">
              {/* All Tab */}
              <button
                onClick={() => setActiveAccountTab('all')}
                className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  activeAccountTab === 'all'
                    ? 'bg-purple-600 text-white border-2 border-purple-400'
                    : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                }`}
                data-testid="calendar-tab-all"
              >
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                All Calendars
                <span className={`text-[10px] md:text-xs ${activeAccountTab === 'all' ? 'text-white/80' : 'text-gray-500'}`}>
                  ({outlookEvents.length + nylasEvents.length})
                </span>
              </button>
              
              {/* Outlook Desktop Tab - Show when synced */}
              {outlookSyncStatus.synced && (
                <button
                  onClick={() => setActiveAccountTab('outlook')}
                  className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                    activeAccountTab === 'outlook'
                      ? 'bg-blue-600 text-white border-2 border-blue-400'
                      : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                  }`}
                  data-testid="calendar-tab-outlook"
                  title="Outlook Desktop Calendar"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0078D4">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                  </svg>
                  <span>Outlook</span>
                  <span className={`text-[10px] md:text-xs ${activeAccountTab === 'outlook' ? 'text-white/80' : 'text-gray-500'}`}>
                    ({outlookEvents.length})
                  </span>
                </button>
              )}
              
              {/* Individual Nylas Account Tabs */}
              {nylasAccounts.map((account) => {
                const accountEventCount = nylasEvents.filter(e => e.account_email === account.email).length;
                const isActive = activeAccountTab === account.email;
                
                return (
                  <button
                    key={account.email}
                    onClick={() => setActiveAccountTab(account.email)}
                    className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                      isActive
                        ? 'bg-purple-600 text-white border-2 border-purple-400'
                        : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid={`calendar-tab-${account.email}`}
                    title={account.email}
                  >
                    {/* Provider Icon */}
                    {account.provider === 'google' ? (
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    ) : account.provider === 'microsoft' || account.provider === 'outlook' ? (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0078D4">
                        <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                      </svg>
                    ) : (
                      <span className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center text-[10px] text-white font-bold">
                        {account.email.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="max-w-[120px] md:max-w-[180px] truncate">
                      {account.account_name || account.email.split('@')[0]}
                    </span>
                    <span className={`text-[10px] md:text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                      ({accountEventCount})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Row 3: Category Filter Tabs */}
        {(outlookSyncStatus.synced || nylasEvents.length > 0) && (
          <div className="flex gap-1 md:gap-2 flex-wrap overflow-x-auto pb-1 items-center">
            {availableOutlookCategories.map((cat) => {
              const outlookCount = cat.id === 'all' ? outlookEvents.length : (categoryCounts[cat.id] || 0);
              const nylasCount = cat.id === 'all' ? nylasEvents.length : 0;
              const count = outlookCount + nylasCount;
              const isActive = activeCategory === cat.id;
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  data-testid={`calendar-category-${cat.id}`}
                  className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-medium transition-all flex items-center gap-1 md:gap-2 whitespace-nowrap ${
                    isActive
                      ? `${getColorClasses(cat.color)} text-white border-2`
                      : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {cat.icon && <span className="text-xs md:text-sm">{cat.icon}</span>}
                  {!cat.icon && <span className={`w-1.5 md:w-2 h-1.5 md:h-2 rounded-full ${getColorClasses(cat.color).split(' ')[0]}`}></span>}
                  {cat.label}
                  <span className={`text-[9px] md:text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>({count})</span>
                </button>
              );
            })}
            
            {/* Edit Categories Button */}
            <button
              onClick={() => setShowCategoryModal(true)}
              data-testid="edit-calendar-categories-btn"
              className="px-2 py-1 md:py-1.5 rounded-md text-[10px] md:text-xs font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
              title="Edit Categories"
            >
              <Pencil size={12} />
              <span className="hidden md:inline">Edit</span>
            </button>
          </div>
        )}
        
        {/* No Calendar Connected Message */}
        {!outlookSyncStatus.synced && nylasAccounts.length === 0 && (
          <div className="flex items-center gap-2 md:gap-3 text-[10px] md:text-sm text-gray-400">
            <Cloud size={14} className="text-gray-500 flex-shrink-0" />
            <span>No calendar connected • Connect email in Communications to auto-sync calendars</span>
          </div>
        )}
      </div>

      {/* Calendar Widget */}
      <div className="bg-black border border-gray-400 rounded-xl p-2 md:p-6 mb-3 md:mb-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex-1 overflow-auto">
        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-0.5 md:gap-2">
          {/* Day Headers */}
          {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day) => (
            <div key={day} className="text-center text-[8px] md:text-xs font-bold text-gray-400 py-1 md:py-2 border-b border-gray-400/20">
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{day.charAt(0)}</span>
            </div>
          ))}

          {/* Calendar Days */}
          {/* Empty cells for days before month starts */}
          {[...Array(monthStartDay)].map((_, i) => (
            <div key={`empty-${i}`} className="min-h-[60px] md:min-h-[100px]"></div>
          ))}

          {/* Month Days */}
          {[...Array(currentDaysInMonth)].map((_, i) => {
            const day = i + 1;
            const isToday = currentMonth === 0 && day === 1 && currentYear === 2026; // Example
            const dayEvents = getEventsForDay(day);
            
            return (
              <div
                key={day}
                onClick={() => {
                  if (dayEvents.length > 0) {
                    handleShowDayEvents(day, dayEvents);
                  }
                }}
                className={`min-h-[60px] md:min-h-[100px] border border-gray-400/20 rounded-lg p-1 md:p-2 hover:bg-white/10 transition-colors cursor-pointer ${
                  isToday ? 'bg-purple-600/20 border-purple-400' : 'bg-black'
                }`}
              >
                <div className={`text-xs md:text-sm font-semibold mb-0.5 md:mb-1 ${isToday ? 'text-purple-300' : 'text-gray-300'}`}>
                  {day}
                </div>
                {dayEvents.length > 0 && (
                  <div className="flex flex-col gap-0.5 md:gap-1">
                    {dayEvents.slice(0, 2).map((event, idx) => (
                      <div
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventClick(event, day);
                        }}
                        className={`px-1 md:px-1.5 py-0.5 md:py-1 rounded text-[9px] md:text-xs font-medium truncate cursor-pointer hover:opacity-80 transition-opacity relative ${
                          getColorClasses(getCategoryColor(event.category || event.type || 'other'))
                        } text-white`}
                        title={`${event.name} (${getCategoryLabel(event.category || event.type || 'other')})`}
                      >
                        <span className="hidden md:inline">{event.name}</span>
                        <span className="md:hidden">{event.name.length > 8 ? event.name.substring(0, 8) + '...' : event.name}</span>
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowDayEvents(day, dayEvents);
                        }}
                        className="text-[8px] md:text-xs text-blue-400 hover:text-blue-300 text-center cursor-pointer hover:underline transition-colors"
                      >
                        +{dayEvents.length - 2} more
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend - Dynamic based on available categories */}
        <div className="flex items-center justify-center gap-2 md:gap-4 mt-2 md:mt-4 pt-2 md:pt-4 border-t border-white/20 flex-wrap">
          {availableOutlookCategories.slice(1, 6).map((cat) => (
            <div key={cat.id} className="flex items-center gap-1 md:gap-2">
              <div className={`w-2 md:w-3 h-2 md:h-3 rounded ${getColorClasses(cat.color).split(' ')[0]}`}></div>
              <span className="text-[10px] md:text-xs text-gray-400">{cat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-2 md:p-4 z-50" onClick={() => setShowEventModal(false)}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-2xl w-full p-4 md:p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button 
              onClick={() => setShowEventModal(false)}
              className="absolute top-2 md:top-4 right-2 md:right-4 text-white hover:bg-white hover:text-black p-1.5 md:p-2 rounded transition-colors"
            >
              <X size={20} className="md:hidden" />
              <X size={24} className="hidden md:block" />
            </button>

            {/* Event Header */}
            <div className="mb-4 md:mb-6 pr-8">
              <div className={`inline-block px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold mb-2 md:mb-3 ${
                selectedEvent.color === 'green' ? 'bg-green-600 text-white' :
                selectedEvent.color === 'blue' ? 'bg-blue-600 text-white' :
                selectedEvent.color === 'yellow' ? 'bg-yellow-600 text-black' :
                selectedEvent.color === 'orange' ? 'bg-orange-600 text-white' :
                selectedEvent.color === 'red' ? 'bg-red-600 text-white' :
                selectedEvent.color === 'pink' ? 'bg-pink-600 text-white' :
                selectedEvent.color === 'purple' ? 'bg-purple-600 text-white' :
                'bg-gray-600 text-white'
              }`}>
                {selectedEvent.type.toUpperCase()}
              </div>
              <h2 className="text-xl md:text-3xl font-bold text-white mb-1 md:mb-2">{selectedEvent.name}</h2>
              <p className="text-gray-400 text-xs md:text-sm">
                {selectedEvent.month} {selectedEvent.day}, {selectedEvent.year}
              </p>
            </div>

            {/* Event Details Grid */}
            <div className="space-y-3 md:space-y-4">
              {/* Time */}
              <div className="flex items-start gap-2 md:gap-3">
                <Clock className="text-blue-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                <div>
                  <div className="text-xs md:text-sm font-semibold text-gray-400">TIME</div>
                  <div className="text-white text-sm md:text-base">{selectedEvent.time}</div>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-2 md:gap-3">
                <MapPin className="text-green-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                <div>
                  <div className="text-xs md:text-sm font-semibold text-gray-400">LOCATION</div>
                  <div className="text-white text-sm md:text-base">{selectedEvent.location}</div>
                </div>
              </div>

              {/* Description */}
              <div className="flex items-start gap-2 md:gap-3">
                <Calendar className="text-purple-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                <div>
                  <div className="text-xs md:text-sm font-semibold text-gray-400">DESCRIPTION</div>
                  <div className="text-white text-sm md:text-base">{selectedEvent.description}</div>
                </div>
              </div>

              {/* Value (for business events) */}
              {selectedEvent.value && (
                <div className="flex items-start gap-2 md:gap-3">
                  <DollarSign className="text-yellow-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                  <div>
                    <div className="text-xs md:text-sm font-semibold text-gray-400">VALUE</div>
                    <div className="text-white font-semibold text-sm md:text-base">{selectedEvent.value}</div>
                  </div>
                </div>
              )}

              {/* Notes (for personal events) */}
              {selectedEvent.notes && (
                <div className="flex items-start gap-2 md:gap-3">
                  <AlertTriangle className="text-orange-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                  <div>
                    <div className="text-xs md:text-sm font-semibold text-gray-400">NOTES</div>
                    <div className="text-white text-sm md:text-base">{selectedEvent.notes}</div>
                  </div>
                </div>
              )}

              {/* Attendees */}
              {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                <div className="flex items-start gap-2 md:gap-3">
                  <Users className="text-pink-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                  <div>
                    <div className="text-xs md:text-sm font-semibold text-gray-400">ATTENDEES</div>
                    <div className="text-white text-sm md:text-base">
                      {selectedEvent.attendees.map((attendee, idx) => (
                        <div key={idx} className="py-0.5 md:py-1">• {attendee}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 md:gap-3 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-white/20">
              <button className="flex-1 bg-white text-black py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base hover:bg-gray-200 transition-colors">
                Edit Event
              </button>
              <button className="flex-1 bg-black border border-gray-400 text-white py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base hover:bg-white hover:text-black transition-colors">
                Delete Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Events Modal - Shows all events for a selected day */}
      {showDayEventsModal && selectedDayEvents.day && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-2 md:p-4 z-50" onClick={() => setShowDayEventsModal(false)}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-lg w-full p-4 md:p-6 relative max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button 
              onClick={() => setShowDayEventsModal(false)}
              className="absolute top-2 md:top-4 right-2 md:right-4 text-white hover:bg-white hover:text-black p-1.5 md:p-2 rounded transition-colors"
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="mb-4 md:mb-6 pr-8">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-1">
                {months[currentMonth]} {selectedDayEvents.day}, {currentYear}
              </h2>
              <p className="text-gray-400 text-sm">
                {selectedDayEvents.events.length} event{selectedDayEvents.events.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Events List */}
            <div className="space-y-2 md:space-y-3">
              {selectedDayEvents.events.map((event, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setShowDayEventsModal(false);
                    handleEventClick(event, selectedDayEvents.day);
                  }}
                  className={`p-3 md:p-4 rounded-lg cursor-pointer transition-all hover:scale-[1.02] border-l-4 ${
                    getColorClasses(getCategoryColor(event.category || event.type || 'other')).split(' ')[1]
                  } bg-gray-900 hover:bg-gray-800`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold text-sm md:text-base truncate">
                        {event.name}
                      </h3>
                      {(event.time || event.start_time) && (
                        <div className="flex items-center gap-1 text-gray-400 text-xs md:text-sm mt-1">
                          <Clock size={12} />
                          <span>{event.time || event.start_time}</span>
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-1 text-gray-400 text-xs md:text-sm mt-1">
                          <MapPin size={12} />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-[10px] md:text-xs font-semibold whitespace-nowrap ${
                      getColorClasses(getCategoryColor(event.category || event.type || 'other'))
                    } text-white`}>
                      {getCategoryLabel(event.category || event.type || 'other')}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Close Button at Bottom */}
            <div className="mt-4 md:mt-6 pt-4 border-t border-white/20">
              <button
                onClick={() => setShowDayEventsModal(false)}
                className="w-full bg-gray-800 hover:bg-gray-700 text-white py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Widget Modal */}
      {showWidgetModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowWidgetModal(false)}>
          <div className="bg-black border border-gray-400 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto hover:border-gray-300 transition-all duration-300" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-black border-b border-gray-400 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-2xl font-bold text-white mokoto-font">
                {widgetModalType === 'thisWeek' && 'This Week - All Events'}
                {widgetModalType === 'criticalDeadlines' && 'All Critical Deadlines'}
                {widgetModalType === 'upcomingEvents' && 'Upcoming Major Events (30 Days)'}
                {widgetModalType === 'teamSchedules' && 'Team Schedules - Detailed View'}
              </h2>
              <button
                onClick={() => setShowWidgetModal(false)}
                className="w-8 h-8 bg-white hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                <X size={16} className="text-black" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {widgetModalType === 'thisWeek' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">All events scheduled for this week (Jan 1-7, 2026)</p>
                  {[
                    { name: 'Empire State Building Closing', date: 'JAN 2', value: '$2.5M', desc: 'Tech Corp lease agreement', type: 'deal' },
                    { name: 'Morning Team Standup', date: 'JAN 3', value: 'N/A', desc: 'Daily sync with all team leads', type: 'meeting' },
                    { name: 'Downtown Listing Launch', date: 'JAN 4', value: '$15M', desc: 'Property at 450 Park Avenue', type: 'marketing' },
                    { name: 'Client Presentation - Riverside Tower', date: 'JAN 5', value: '$8.2M', desc: 'Investor pitch meeting', type: 'meeting' },
                    { name: 'Q1 Revenue Review Meeting', date: 'JAN 6', value: 'N/A', desc: 'Finance team quarterly review', type: 'deadline' },
                    { name: 'Brooklyn Development Site Visit', date: 'JAN 7', value: '$12M', desc: 'Construction progress review', type: 'inspection' }
                  ].map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{event.name}</h3>
                          <p className="text-sm text-gray-400">{event.desc}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-blue-400">{event.date}</span>
                          {event.value !== 'N/A' && (
                            <div className="text-lg font-bold text-green-400">{event.value}</div>
                          )}
                        </div>
                      </div>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        event.type === 'deal' ? 'bg-green-500/20 text-green-400' :
                        event.type === 'meeting' ? 'bg-blue-500/20 text-blue-400' :
                        event.type === 'marketing' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {event.type.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {widgetModalType === 'criticalDeadlines' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">All critical deadlines requiring immediate attention</p>
                  {[
                    { name: 'Chelsea Property Contract', days: 2, desc: 'Final contract review deadline', urgency: 'urgent' },
                    { name: 'Brooklyn Warehouse Inspection', days: 5, desc: 'Structural inspection must complete', urgency: 'high' },
                    { name: 'Q4 Financial Report', days: 7, desc: 'Board presentation preparation', urgency: 'medium' },
                    { name: 'Madison Ave Lease Extension', days: 10, desc: 'Tenant renewal decision needed', urgency: 'medium' },
                    { name: 'Zoning Application Submission', days: 12, desc: 'City planning department deadline', urgency: 'high' },
                    { name: 'Property Tax Appeals', days: 14, desc: 'Three properties pending appeals', urgency: 'medium' }
                  ].map((deadline, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`flex-shrink-0 ${
                          deadline.urgency === 'urgent' ? 'text-red-400' :
                          deadline.urgency === 'high' ? 'text-orange-400' :
                          'text-yellow-400'
                        }`} size={24} />
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-semibold text-white">{deadline.name}</h3>
                            <span className={`text-lg font-bold ${
                              deadline.urgency === 'urgent' ? 'text-red-400' :
                              deadline.urgency === 'high' ? 'text-orange-400' :
                              'text-yellow-400'
                            }`}>
                              {deadline.days} DAYS
                            </span>
                          </div>
                          <p className="text-sm text-gray-400 mb-2">{deadline.desc}</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            deadline.urgency === 'urgent' ? 'bg-red-500/20 text-red-400' :
                            deadline.urgency === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {deadline.urgency.toUpperCase()} PRIORITY
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {widgetModalType === 'upcomingEvents' && (
                <div className="space-y-6">
                  <p className="text-gray-400 mb-4">Major events scheduled for the next 30 days</p>
                  
                  {/* Week 2 */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">Week 2 (Jan 8-14, 2026)</h3>
                    <div className="space-y-3">
                      {[
                        { name: 'Madison Ave Office Showing', date: 'Jan 10', value: '$6.2M', type: 'VIP Client Tour' },
                        { name: 'Investment Committee Meeting', date: 'Jan 12', value: 'N/A', type: 'Internal Review' },
                        { name: 'Upper East Side Listing', date: 'Jan 14', value: '$9.8M', type: 'New Property' }
                      ].map((event, idx) => (
                        <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4">
                          <h4 className="text-md font-semibold text-white mb-1">{event.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{event.date}</span>
                            {event.value !== 'N/A' && <span className="text-green-400 font-semibold">{event.value}</span>}
                            <span className="text-blue-400">{event.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Week 3 */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">Week 3 (Jan 15-21, 2026)</h3>
                    <div className="space-y-3">
                      {[
                        { name: 'Board of Directors Meeting', date: 'Jan 18', value: 'N/A', type: 'Strategic Planning' },
                        { name: 'Tribeca Loft Closing', date: 'Jan 19', value: '$4.5M', type: 'Deal Closing' },
                        { name: 'Annual Team Retreat', date: 'Jan 20-21', value: 'N/A', type: 'Team Building' }
                      ].map((event, idx) => (
                        <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4">
                          <h4 className="text-md font-semibold text-white mb-1">{event.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{event.date}</span>
                            {event.value !== 'N/A' && <span className="text-green-400 font-semibold">{event.value}</span>}
                            <span className="text-blue-400">{event.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Week 4 */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">Week 4 (Jan 22-28, 2026)</h3>
                    <div className="space-y-3">
                      {[
                        { name: 'SoHo Retail Closing', date: 'Jan 25', value: '$3.8M', type: 'Final Documents' },
                        { name: 'Q1 Forecast Presentation', date: 'Jan 27', value: 'N/A', type: 'Executive Team' },
                        { name: 'New Listing Open House', date: 'Jan 28', value: '$7.2M', type: 'Marketing Event' }
                      ].map((event, idx) => (
                        <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4">
                          <h4 className="text-md font-semibold text-white mb-1">{event.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{event.date}</span>
                            {event.value !== 'N/A' && <span className="text-green-400 font-semibold">{event.value}</span>}
                            <span className="text-purple-400">{event.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {widgetModalType === 'teamSchedules' && (
                <div className="space-y-6">
                  <p className="text-gray-400 mb-4">Detailed schedule overview for all team members this week</p>
                  {[
                    { name: 'Sarah Mitchell', role: 'Senior Broker', events: 12, meetings: 8, deals: 3, showings: 5 },
                    { name: 'John Martinez', role: 'Deal Manager', events: 9, meetings: 6, deals: 5, showings: 2 },
                    { name: 'Alex Rivera', role: 'Property Manager', events: 15, meetings: 10, deals: 2, showings: 7 },
                    { name: 'Emily Chen', role: 'Marketing Director', events: 8, meetings: 5, deals: 1, showings: 3 },
                    { name: 'Michael Wong', role: 'Financial Analyst', events: 11, meetings: 9, deals: 4, showings: 1 }
                  ].map((member, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-6 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-white mb-1">{member.name}</h3>
                          <p className="text-sm text-gray-400">{member.role}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-white">{member.events}</div>
                          <div className="text-xs text-gray-400">Events This Week</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-400">{member.meetings}</div>
                          <div className="text-xs text-gray-400">Meetings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-400">{member.deals}</div>
                          <div className="text-xs text-gray-400">Deals</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-400">{member.showings}</div>
                          <div className="text-xs text-gray-400">Showings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-400">{member.events - member.meetings - member.deals - member.showings}</div>
                          <div className="text-xs text-gray-400">Other</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowSummaryModal(false)}>
          <div className="bg-black border border-gray-400 rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto hover:border-gray-300 transition-all duration-300" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-black border-b border-gray-400 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-2xl font-bold text-white mokoto-font">
                {summaryModalType === 'total' && 'All Upcoming Events'}
                {summaryModalType === 'critical' && 'Critical Deadlines'}
                {summaryModalType === 'deals' && 'Deal Closings This Month'}
                {summaryModalType === 'value' && 'Active Pipeline ($67M)'}
              </h2>
              <button
                onClick={() => setShowSummaryModal(false)}
                className="w-8 h-8 bg-white hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                <X size={16} className="text-black" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {summaryModalType === 'total' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">Showing all 89 events scheduled for the next 60 days</p>
                  {businessEvents.all.slice(0, 20).map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{event.name}</h3>
                          <p className="text-sm text-gray-400">{event.time}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          event.type === 'deal' ? 'bg-green-500/20 text-green-400' :
                          event.type === 'meeting' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {event.type.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {event.location}
                        </span>
                        {event.value !== 'N/A' && (
                          <span className="flex items-center gap-1">
                            <DollarSign size={14} />
                            {event.value}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {summaryModalType === 'critical' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">8 high-priority items requiring immediate attention</p>
                  {businessEvents.all.filter(e => e.type === 'deadline').map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{event.name}</h3>
                          <p className="text-sm text-gray-400 mb-2">{event.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-red-400 font-semibold">{event.time}</span>
                            <span className="text-gray-400">{event.location}</span>
                            {event.value !== 'N/A' && (
                              <span className="text-green-400 font-semibold">{event.value}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {summaryModalType === 'deals' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">15 deal closings scheduled this month</p>
                  {businessEvents.all.filter(e => e.type === 'deal').map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="text-green-400 flex-shrink-0" size={20} />
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-semibold text-white">{event.name}</h3>
                            <span className="text-2xl font-bold text-green-400">{event.value}</span>
                          </div>
                          <p className="text-sm text-gray-400 mb-2">{event.description}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              Jan {event.day}, 2026
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {event.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {event.location}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <Users size={14} className="text-gray-500" />
                            <span className="text-xs text-gray-500">{event.attendees.join(', ')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {summaryModalType === 'value' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">Active pipeline breakdown by deal value</p>
                  <div className="bg-black border border-gray-400 rounded-lg p-6 mb-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Total Pipeline Value</div>
                        <div className="text-4xl font-bold text-purple-400">$67M</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Active Deals</div>
                        <div className="text-4xl font-bold text-white">15</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Average Deal Size</div>
                        <div className="text-2xl font-bold text-white">$4.5M</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Expected Close</div>
                        <div className="text-2xl font-bold text-green-400">Q1 2026</div>
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-3">High-Value Deals</h3>
                  {businessEvents.all
                    .filter(e => e.type === 'deal')
                    .sort((a, b) => {
                      const valA = parseFloat(a.value.replace(/[$M]/g, ''));
                      const valB = parseFloat(b.value.replace(/[$M]/g, ''));
                      return valB - valA;
                    })
                    .map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-white">{event.name}</h3>
                        <span className="text-2xl font-bold text-green-400">{event.value}</span>
                      </div>
                      <p className="text-sm text-gray-400 mb-2">{event.description}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Closing: Jan {event.day}, 2026</span>
                        <span>•</span>
                        <span>{event.location}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* iCloud Calendar Credentials Modal */}
      {showICloudCalendarModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowICloudCalendarModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Connect iCloud Calendar</h3>
                  <p className="text-sm text-gray-400">Sign in to sync your calendar events</p>
                </div>
              </div>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              if (icloudCalEmail && icloudCalPassword) {
                handleICloudCalendarConnect();
              }
            }} className="p-6 space-y-4">
              {calendarError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {calendarError}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Apple ID (Email)</label>
                <input
                  type="email"
                  value={icloudCalEmail}
                  onChange={(e) => setIcloudCalEmail(e.target.value)}
                  placeholder="your@icloud.com"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  required
                  data-testid="icloud-cal-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">App-Specific Password</label>
                <input
                  type="password"
                  value={icloudCalPassword}
                  onChange={(e) => setIcloudCalPassword(e.target.value)}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  required
                  data-testid="icloud-cal-password-input"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Generate an app-specific password at{' '}
                  <a 
                    href="https://appleid.apple.com/account/manage" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline"
                  >
                    appleid.apple.com
                  </a>
                  {' '}→ Sign-In & Security → App-Specific Passwords
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowICloudCalendarModal(false);
                    setCalendarError('');
                  }}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={calendarConnecting || !icloudCalEmail || !icloudCalPassword}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  data-testid="icloud-cal-connect-submit"
                >
                  {calendarConnecting ? (
                    <>
                      <RefreshCw size={18} className="animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Cloud size={18} />
                      Connect
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Google Calendar Connection Modal */}
      {showGoogleCalendarModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowGoogleCalendarModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white rounded-lg">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Connect Google Calendar</h3>
                  <p className="text-sm text-gray-400">Sync your Google Calendar events</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">How to Connect:</h4>
                <div className="space-y-3 text-sm text-gray-300">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                    <p>Click the "Connect with Google" button below</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
                    <p>Sign in to your Google account</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
                    <p>Allow access to your Google Calendar</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">4</span>
                    <p>Your events will sync automatically</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-xs text-blue-400">
                  <strong>Note:</strong> Google Calendar integration uses OAuth for secure authentication. Your credentials are never stored on our servers.
                </p>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowGoogleCalendarModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  window.open('https://calendar.google.com', '_blank');
                  // For now, mark as connected for demo
                  setIsCalendarConnected(true);
                  setConnectedCalendarType('google');
                  localStorage.setItem('calendar_connected_type', 'google');
                  setShowGoogleCalendarModal(false);
                }}
                className="flex-1 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Connect with Google
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Outlook Calendar Connection Modal */}
      {showOutlookCalendarModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowOutlookCalendarModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-[#0078D4] rounded-lg">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576zM14.637 7.387v13.226H.786c-.226 0-.42-.076-.578-.23C.05 20.23 0 20.036 0 19.81V5.618c0-.226.076-.42.23-.577C.383 4.888.577 4.81.803 4.81h13.834v2.577zm-2.46 5.618c0-.805-.284-1.49-.854-2.056-.57-.566-1.257-.85-2.06-.85-.805 0-1.492.284-2.062.85-.57.566-.855 1.251-.855 2.056 0 .8.285 1.485.855 2.051.57.566 1.257.85 2.062.85.803 0 1.49-.284 2.06-.85.57-.566.854-1.251.854-2.051zm-1.23 0c0 .453-.16.84-.484 1.163-.32.323-.71.484-1.166.484-.457 0-.847-.161-1.17-.484-.324-.322-.486-.71-.486-1.163 0-.453.162-.84.486-1.163.323-.323.713-.484 1.17-.484.456 0 .845.161 1.166.484.323.322.484.71.484 1.163z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Connect Outlook Calendar</h3>
                  <p className="text-sm text-gray-400">Sync your Outlook calendar events</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">Sync via Desktop App:</h4>
                <div className="space-y-3 text-sm text-gray-300">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                    <p>Make sure Outlook Desktop is running on your Mac</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
                    <p>Run the Outlook Calendar sync shortcut</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
                    <p>Your events will be synced to this dashboard</p>
                  </div>
                </div>
              </div>
              
              {outlookSyncStatus.synced && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="text-green-400" size={18} />
                  <div>
                    <p className="text-sm text-green-400 font-medium">Outlook Already Connected</p>
                    <p className="text-xs text-gray-400">
                      Last sync: {new Date(outlookSyncStatus.last_sync).toLocaleDateString()} • {outlookSyncStatus.event_count} events
                    </p>
                  </div>
                </div>
              )}
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-xs text-blue-400">
                  <strong>Note:</strong> Outlook sync uses AppleScript automation on your Mac. Events are read directly from the Outlook app.
                </p>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowOutlookCalendarModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={connectOutlookCalendar}
                disabled={syncingOutlook}
                className="flex-1 px-4 py-3 bg-[#0078D4] hover:bg-[#106EBE] disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {syncingOutlook ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Syncing...
                  </>
                ) : outlookSyncStatus.synced ? (
                  <>
                    <RefreshCw size={18} />
                    Sync Again
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                    </svg>
                    Connect Outlook
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Event Modal */}
      {showAddEventModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowAddEventModal(false);
            resetEventForm();
          }}
        >
          <div 
            className="bg-gray-900 rounded-2xl w-full max-w-lg border border-gray-700 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Plus size={20} className="text-purple-400" />
                Add New Event
              </h3>
              <button
                onClick={() => {
                  setShowAddEventModal(false);
                  resetEventForm();
                }}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {createEventError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {createEventError}
                </div>
              )}
              
              {/* Event Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Event Title *</label>
                <input
                  type="text"
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value)}
                  placeholder="e.g., Team Meeting"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  data-testid="event-title-input"
                />
              </div>
              
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Date *</label>
                <input
                  type="date"
                  value={newEventDate}
                  onChange={(e) => setNewEventDate(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  data-testid="event-date-input"
                />
              </div>
              
              {/* Time Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={newEventStartTime}
                    onChange={(e) => setNewEventStartTime(e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                    data-testid="event-start-time-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">End Time</label>
                  <input
                    type="time"
                    value={newEventEndTime}
                    onChange={(e) => setNewEventEndTime(e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                    data-testid="event-end-time-input"
                  />
                </div>
              </div>
              
              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    value={newEventLocation}
                    onChange={(e) => setNewEventLocation(e.target.value)}
                    placeholder="e.g., Conference Room A"
                    className="w-full pl-10 pr-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                    data-testid="event-location-input"
                  />
                </div>
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={newEventDescription}
                  onChange={(e) => setNewEventDescription(e.target.value)}
                  placeholder="Add event details..."
                  rows={3}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none resize-none"
                  data-testid="event-description-input"
                />
              </div>
              
              {/* Info Box */}
              {nylasAccounts.length === 0 && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-400 text-sm">
                    <AlertTriangle size={14} className="inline mr-2" />
                    Connect an email account to sync events to your calendar.
                  </p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowAddEventModal(false);
                  resetEventForm();
                }}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createNewEvent}
                disabled={creatingEvent || !newEventTitle.trim() || !newEventDate}
                className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="create-event-btn"
              >
                {creatingEvent ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    Create Event
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Category Management Modal */}
      {showCategoryModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowCategoryModal(false)}
        >
          <div 
            className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Calendar Categories</h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                data-testid="close-category-modal-btn"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            {/* Add New Category */}
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                {/* Icon Selector */}
                <div className="relative">
                  <button
                    type="button"
                    className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center hover:border-gray-500"
                    onClick={(e) => {
                      const dropdown = e.currentTarget.nextElementSibling;
                      dropdown.classList.toggle('hidden');
                    }}
                    data-testid="category-icon-selector"
                  >
                    {newCategoryIcon}
                  </button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-6 gap-1 w-48">
                    {calendarCategoryIcons.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={(e) => {
                          setNewCategoryIcon(icon);
                          e.currentTarget.parentElement.classList.add('hidden');
                        }}
                        className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center"
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCalendarCategory()}
                  placeholder="New category name..."
                  className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  data-testid="new-calendar-category-input"
                />
              </div>
              
              {/* Color Selector */}
              <div className="flex gap-2 items-center mb-3">
                <span className="text-xs text-gray-400">Color:</span>
                <div className="flex gap-1 flex-wrap">
                  {categoryColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewCategoryColor(color)}
                      className={`w-6 h-6 rounded-full ${getColorClasses(color).split(' ')[0]} ${newCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : 'hover:opacity-80'}`}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              
              <button
                onClick={addCalendarCategory}
                disabled={!newCategoryName.trim()}
                className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
                data-testid="add-calendar-category-btn"
              >
                <Plus size={14} />
                Add Category
              </button>
            </div>
            
            {/* Category List */}
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customCalendarCategories.filter(c => c.id !== 'all').map((category) => {
                const protectedCategories = ['meeting', 'work', 'other'];
                const isProtected = protectedCategories.includes(category.id);
                
                return (
                  <div 
                    key={category.id}
                    className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group"
                  >
                    {editingCategory === category.id ? (
                      <>
                        <span className="text-lg">{category.icon}</span>
                        <input
                          type="text"
                          value={editCategoryValue}
                          onChange={(e) => setEditCategoryValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') updateCalendarCategory(category.id, editCategoryValue, category.icon, category.color);
                            if (e.key === 'Escape') {
                              setEditingCategory(null);
                              setEditCategoryValue('');
                            }
                          }}
                          className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                          autoFocus
                          data-testid={`edit-calendar-category-input-${category.id}`}
                        />
                        <button
                          onClick={() => updateCalendarCategory(category.id, editCategoryValue, category.icon, category.color)}
                          className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                          title="Save"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setEditCategoryValue('');
                          }}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                          title="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className={`w-8 h-8 rounded-lg ${getColorClasses(category.color).split(' ')[0]} flex items-center justify-center text-sm`}>
                          {category.icon}
                        </span>
                        <span className="flex-1 text-white text-sm">
                          {category.label}
                          {isProtected && (
                            <span className="ml-2 text-xs text-gray-500">(default)</span>
                          )}
                        </span>
                        <button
                          onClick={() => {
                            setEditingCategory(category.id);
                            setEditCategoryValue(category.label);
                          }}
                          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Edit"
                          data-testid={`edit-calendar-category-btn-${category.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        {!isProtected && (
                          <button
                            onClick={() => deleteCalendarCategory(category.id)}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete"
                            data-testid={`delete-calendar-category-btn-${category.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
                data-testid="done-calendar-category-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RealEstateManagingPartnerCalendar;
